import React from 'react';
import dayjs from 'dayjs';

import { VEHICLE_FACT_SOURCE, quoteProgram, type LaneTollConfig, type ProgramQuote } from '@hopdrive/sdk-ar';

import { decodeVin } from '@utils/decodeVin/decodeVin';

import { resolveProgramVisuals, type ProgramVisualsConfig } from '@features/programs/programVisuals';
import { useBillingAuthority } from '@features/moveCreation/hooks/useBillingAuthority';
import { useIntendedProduct, useMovePlanner } from '@hopdrive/feature-move-planner';
import {
  isVehicleReady,
  missingVehicleFacts,
  type PlannerVehicle,
  type VehicleFact,
} from '@features/moveCreation/lib/programVehicleEligibility';

/**
 * Live program-coverage quote for the move planner. Reads the planner context
 * (customer, vehicle, lane) and runs sdk-ar `quoteProgram` — the same
 * split/settlement the persisted AR uses, so the create-time estimate can't
 * diverge from what bills later.
 *
 * Debounced because the vehicle make/VIN come from free-text inputs. The
 * coverage bar, the lane estimate strip and the eligibility checklist all read
 * this hook, so identical requests share one in-flight quote.
 */
const QUOTE_DEBOUNCE_MS = 400;

/**
 * Program enrollment and coverage contracts are date-windowed, so the chosen
 * pickup time is the truthful input. Until a time is picked there is nothing
 * better than now, and a booking near a contract boundary can show an estimate
 * that changes once the time is set.
 */
export function plannerBillingDate(pickupTime: string | null | undefined): string {
  const picked = pickupTime ? dayjs(pickupTime) : null;
  return picked && picked.isValid() ? picked.toISOString() : new Date().toISOString();
}

const inFlightQuotes = new Map<string, Promise<ProgramQuote | null>>();
const sharedQuote = (key: string, run: () => Promise<ProgramQuote | null>) => {
  const existing = inFlightQuotes.get(key);
  if (existing) return existing;
  const pending = run().finally(() => inFlightQuotes.delete(key));
  inFlightQuotes.set(key, pending);
  return pending;
};

/** What the VIN decode says the vehicle is. A null make means it couldn't tell. */
interface DecodedVehicle {
  make: string | null;
  year: number | null;
}

const FULL_VIN_LENGTH = 17;

// Per VIN, so typing elsewhere in the form never decodes the same VIN twice,
// and the pinned bar and the summary (one hook each) share one request.
const decodedVehicles = new Map<string, DecodedVehicle>();
const inFlightDecodes = new Map<string, Promise<DecodedVehicle>>();

/** Normalized as decodeVin normalizes it; null unless it is a full VIN. */
function fullVinOf(vin: string | null | undefined): string | null {
  const normalized = vin?.trim().toUpperCase() ?? '';
  return normalized.length === FULL_VIN_LENGTH ? normalized : null;
}

function decodePlannerVin(vin: string): Promise<DecodedVehicle> {
  const existing = inFlightDecodes.get(vin);
  if (existing) return existing;
  const pending = decodeVin(vin, true)
    .then(result => {
      const decoded = result as { vehicle_make?: unknown; vehicle_year?: unknown };
      const make = typeof decoded?.vehicle_make === 'string' ? decoded.vehicle_make.trim() : '';
      return { make: make || null, year: Number(decoded?.vehicle_year) || null };
    })
    .catch(() => ({ make: null, year: null }))
    .then(decoded => {
      // A decode that came back empty (a timeout, an unknown VIN) isn't kept,
      // so the next planner to see the VIN tries again.
      if (decoded.make) decodedVehicles.set(vin, decoded);
      return decoded;
    })
    .finally(() => inFlightDecodes.delete(vin));
  inFlightDecodes.set(vin, pending);
  return pending;
}

/**
 * The full VIN's decode, or null without one. `decoding` is true while it is
 * in flight, so nothing is judged from the typed make in the meantime.
 */
function usePlannerVinDecode(vin: string | null): { decoding: boolean; decoded: DecodedVehicle | null } {
  const [settled, setSettled] = React.useState<{ vin: string; decoded: DecodedVehicle } | null>(null);
  React.useEffect(() => {
    if (!vin || decodedVehicles.has(vin)) return;
    let cancelled = false;
    decodePlannerVin(vin).then(decoded => {
      if (!cancelled) setSettled({ vin, decoded });
    });
    return () => {
      cancelled = true;
    };
  }, [vin]);
  if (!vin) return { decoding: false, decoded: null };
  const decoded = decodedVehicles.get(vin) ?? (settled?.vin === vin ? settled.decoded : undefined);
  return { decoding: decoded === undefined, decoded: decoded ?? null };
}

export interface UseProgramCoverageQuoteResult {
  quote: ProgramQuote | null;
  loading: boolean;
  visuals: ProgramVisualsConfig;
  programGoverns: boolean;
  /**
   * The rooftop holds both Valet tiers and nothing says which one this move is,
   * so no quote is possible. The coverage bar names the two products rather
   * than showing a Full Service estimate the move may not bill.
   */
  productAmbiguous: boolean;
  candidateProductKeys: string[];
  /**
   * Whether the quote is in and none of the vehicle facts the contract's
   * eligibility reads (make, year, VIN) is still assumed. Until then the VIN
   * decode can flip eligibility and so what the dealer pays, so neither the
   * coverage bar nor the lane strip shows a figure.
   */
  vehicleReady: boolean;
  /** The vehicle facts the quote is still assuming, for the prompts. */
  missingVehicleFacts: VehicleFact[];
  /**
   * The customer vehicle's make and year as the quote judged them, for the
   * eligibility checklist to quote back: the VIN's decode when it has one,
   * else what was typed.
   */
  vehicle: PlannerVehicle;
  /** e.g. "2024 Genesis" for labeling which vehicle the estimate is for, decoded from the VIN when it can be. */
  vehicleLabel: string | null;
  /**
   * The lane distance `quote` was asked to price, so the breakdown can show why
   * a long move costs the dealer more. It is the lane's own distance, the one
   * the estimate strip shows; a lane modifier quoteProgram applies is not in it.
   * Null when there is no quote.
   */
  distanceMiles: number | null;
}

export function useProgramCoverageQuote(): UseProgramCoverageQuoteResult {
  const ctx = useMovePlanner();
  const rooftopId = ctx.rooftopId;
  const lane = ctx.lane;
  const rooftops = ctx.rooftops;

  // Coverage is about the CUSTOMER's vehicle, not the loaner. In a loaner move
  // the loaner can be move 1 (vehicle1); the customer's car is then vehicle2.
  // Pick the customer leg by the move title so we never quote the loaner.
  const vehicle = ctx.move1Title === 'LOANER MOVE' ? ctx.vehicle2 : ctx.vehicle1;

  // The flag is read off the rooftop's merged config so a program-level
  // setting reaches every enrolled dealer.
  const rooftop = React.useMemo(
    () => (rooftops ?? []).find(r => Number(r?.id) === Number(rooftopId)),
    [rooftops, rooftopId],
  );
  const visuals = React.useMemo(
    () => resolveProgramVisuals(rooftop?.effective_config),
    [rooftop],
  );

  const distanceMiles = lane?.distance_miles ?? null;
  const laneTolls = lane?.tolls ?? null;
  const customerConfig = rooftop?.effective_config as LaneTollConfig | null | undefined;
  // Where the move sits, for the lane modifier the realized AR would apply.
  const workflowsetId = ctx.workflowsetId || null;
  const workflowsetType = (ctx.workflowsets ?? []).find(w => w?.id === workflowsetId)?.type ?? null;
  const organizationId = rooftop?.organization_id ?? null;
  const enteredMake = vehicle?.make ?? null;
  const vin = vehicle?.vin ?? null;
  // Planner stores year as a string; quoteProgram wants number | null.
  const enteredYear = vehicle?.year ? Number(vehicle.year) || null : null;
  // The RO the move will carry, picked as plannerSubmit picks it: a Valet move
  // takes the consumer's. Lets a contract's RO rule pass before creation.
  const isConcierge = ctx.type === 'concierge' || ctx.type === 'concierge-loaner';
  const referenceNum = (isConcierge && ctx.consumer?.referenceId) || vehicle?.referenceId || null;

  const asOf = React.useMemo(() => plannerBillingDate(ctx.time), [ctx.time]);
  // Resolved the way the Action resolves it at creation, so the resolver and
  // the quote price against the contract the move will bill on.
  const intended = useIntendedProduct();
  const { programGoverns, productAmbiguous, candidateProductKeys } = useBillingAuthority(
    rooftopId,
    undefined,
    asOf,
    intended.productKey,
  );

  // The program reads the make and year off the VIN, not the form, so a typed
  // "Genesis" over a Hyundai VIN can't make the move eligible. The typed values
  // stand in only without a full VIN or when the decode names no make. Decoded
  // only where a program governs, since nothing else reads it.
  const { decoding, decoded } = usePlannerVinDecode(programGoverns ? fullVinOf(vin) : null);
  const decodedMake = decoded?.make ? decoded : null;
  const make = decodedMake ? decodedMake.make : enteredMake;
  const year = decodedMake ? (decodedMake.year ?? enteredYear) : enteredYear;
  const factSource = decodedMake ? VEHICLE_FACT_SOURCE.VIN_DECODE : VEHICLE_FACT_SOURCE.ENTERED;

  const [quote, setQuote] = React.useState<ProgramQuote | null>(null);
  // Kept with the quote rather than read off the lane, so while a new lane is
  // re-quoting the distance still matches the figures on screen.
  const [quotedDistanceMiles, setQuotedDistanceMiles] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Without a lane distance the quote projects all-$0, so it is skipped.
  const enabled =
    programGoverns &&
    typeof rooftopId === 'number' &&
    rooftopId > 0 &&
    typeof distanceMiles === 'number' &&
    distanceMiles > 0;

  React.useEffect(() => {
    if (!enabled) {
      setQuote(null);
      setQuotedDistanceMiles(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Quoting the typed make now would flash a verdict the decode may reverse.
    if (decoding) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const noTollCharges = customerConfig?.routing?.no_toll_charges === true;
        const key = [rooftopId, distanceMiles, laneTolls, noTollCharges, workflowsetId, workflowsetType, organizationId, make, vin, year, factSource, referenceNum, asOf, intended.productKey].join('|');
        const result = await sharedQuote(key, () =>
          quoteProgram({
            customerId: rooftopId as number,
            vehicle: { make, vin, year, factSource },
            referenceNum,
            distanceMiles,
            laneTolls,
            customerConfig,
            productKey: intended.productKey,
            laneModifierScope: { workflowsetId, workflowsetType, organizationId },
            asOf,
          }),
        );
        if (!cancelled) {
          setQuote(result);
          setQuotedDistanceMiles(result ? distanceMiles : null);
        }
      } catch (err) {
        if (!cancelled) {
          setQuote(null);
          setQuotedDistanceMiles(null);
        }
        console.error(`useProgramCoverageQuote: quoteProgram failed`, err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [enabled, decoding, rooftopId, distanceMiles, laneTolls, customerConfig, workflowsetId, workflowsetType, organizationId, make, vin, year, factSource, referenceNum, asOf, intended.productKey]);

  const vehicleLabel = [year, make].filter(Boolean).join(' ') || null;

  return {
    quote,
    loading,
    visuals,
    programGoverns,
    productAmbiguous,
    candidateProductKeys,
    vehicleReady: isVehicleReady(quote),
    missingVehicleFacts: missingVehicleFacts(quote),
    vehicle: { make, year, factSource, enteredMake },
    vehicleLabel,
    distanceMiles: quotedDistanceMiles,
  };
}
